import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { GameState } from "../core/game-state"
import { EVENT } from "../core/keys"
import { THEME } from "../core/theme"
import {
  BALL_OVERSPEED_DECAY,
  BALL_TYPE_DEFS,
  BUMPER_POP_SPEED_MULT,
  type BallType,
  GAME_HEIGHT,
  GAME_WIDTH,
  KICKBACK_LIFETIME_RESTORE,
  KICKBACK_SPEED_MULT,
  SCENE_TUNING,
  SLINGSHOT_KICK_SPEED_MULT,
} from "../data/constants"
import type { BallDefinition } from "../data/constants"
import { ENEMY_DEFINITIONS } from "../data/enemies"
import type {
  BumperEntityData,
  CombatBallEntityData,
  EnemyData,
  GameSprite,
  HeroControlProfile,
  Player,
} from "../types"
import type { BumperSystem } from "./bumper-system"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors

const getCombatBallData = (
  sprite: Phaser.Physics.Arcade.Sprite,
): CombatBallEntityData | undefined =>
  (sprite as unknown as { entityData?: CombatBallEntityData }).entityData

const setCombatBallData = (
  sprite: Phaser.Physics.Arcade.Sprite,
  entityData: CombatBallEntityData,
): void => {
  ;(sprite as unknown as { entityData?: CombatBallEntityData }).entityData =
    entityData
}

const getBumperData = (
  sprite: Phaser.Physics.Arcade.Sprite,
): BumperEntityData | undefined =>
  (sprite as unknown as { entityData?: BumperEntityData }).entityData

const getEnemyData = (
  sprite: Phaser.Physics.Arcade.Sprite,
): EnemyData | undefined =>
  (sprite as unknown as { entityData?: EnemyData }).entityData

const BALL_WALL_PADDING = SCENE_TUNING.bouncingBallBoundsPadding

/** Minimum speed ratio — ball never drops below 65% of base speed */
const MIN_SPEED_RATIO = 0.65

/**
 * Minimum vertical fraction of horizontal speed after a top/bottom wall bounce.
 * Prevents degenerate near-horizontal trajectories (ball stuck in dead flat loop).
 * Real pinball tables rely on their geometry to break these up; we enforce a soft floor.
 */
const MIN_BOUNCE_VY_FRACTION = 0.22

/** AoE radius for massive splash (px) */
const MASSIVE_SPLASH_RADIUS = 100
/** Fraction of ball damage applied as splash */
const SPLASH_DAMAGE_RATIO = 0.5
const OVERDRIVE_BOSS_DAMAGE_SCALE = 0.2
const BALL_DAMAGE_SIZE_BASELINE = 0.4
const BALL_DAMAGE_SIZE_MIN_MULTIPLIER = 0.75
const BALL_DAMAGE_SIZE_MAX_MULTIPLIER = 2.5

const FIREBALL_COLOR = 0xf97316
const FIREBALL_CORE = 0xfff7ed
const FIREBALL_FLARE = 0xfb923c

const BALL_SPEED_RAMP_TUNING = {
  initialSpeedRatio: 0.6,
  perWaveBonus: 0.05,
  maxWaveBonus: 0.7,
  intraWaveBonus: 0.2,
} as const

// ── System ──────────────────────────────────────────────────────

export class CombatBallSystem {
  private scene: Phaser.Scene
  private combatBallGroup: Phaser.Physics.Arcade.Group
  private launcher: BallDefinition
  private vfx: VfxManager
  private heroProfile: HeroControlProfile
  private onEnemyDefeated: (enemySprite: GameSprite) => void
  /** Optional callback for boss-specific hit handling. Return true if damage was fully absorbed (e.g. brain shield), false to apply normal HP deduction. */
  private onBossHit?: (enemy: GameSprite, damage: number) => boolean

  /** Monotonic launch counter for age ordering */
  private launchCounter = 0

  constructor(
    scene: Phaser.Scene,
    combatBallGroup: Phaser.Physics.Arcade.Group,
    launcher: BallDefinition,
    _bumperSystem: BumperSystem,
    vfx: VfxManager,
    heroProfile: HeroControlProfile,
    onEnemyDefeated: (enemySprite: GameSprite) => void,
    onBossHit?: (enemy: GameSprite, damage: number) => boolean,
  ) {
    this.scene = scene
    this.combatBallGroup = combatBallGroup
    this.launcher = launcher
    this.vfx = vfx
    this.heroProfile = heroProfile
    this.onEnemyDefeated = onEnemyDefeated
    this.onBossHit = onBossHit
  }

  private getPlayer(): Player | null {
    const waveScene = this.scene as Phaser.Scene & {
      getGameData?: () => { player?: Player }
    }
    return (
      waveScene.getGameData?.()?.player ?? GameState.instance.player ?? null
    )
  }

  private getProjectileSpeedMultiplier(): number {
    const player = this.getPlayer()
    return Phaser.Math.Clamp(player?.stats.attackSpeed ?? 1, 0.4, 4.5)
  }

  private getCritChance(): number {
    return 0
  }

  private getCritDamageMultiplier(): number {
    return 1
  }

  private getBallDamage(ballSizeMultiplier: number): number {
    const player = this.getPlayer()
    const sizeMultiplier = Phaser.Math.Clamp(
      Math.sqrt(Math.max(0.01, ballSizeMultiplier) / BALL_DAMAGE_SIZE_BASELINE),
      BALL_DAMAGE_SIZE_MIN_MULTIPLIER,
      BALL_DAMAGE_SIZE_MAX_MULTIPLIER,
    )
    const passiveMultiplier = 1 + (player?.passives.damageMultiplier ?? 0)

    return Math.max(
      1,
      Math.round(this.launcher.baseDamage * sizeMultiplier * passiveMultiplier),
    )
  }

  private getDifficultySpeedRatio(): number {
    const waveScene = this.scene as Phaser.Scene & {
      getGameData?: () => { wave?: number }
      getWaveProgress?: () => number
    }
    const wave = Math.max(1, waveScene.getGameData?.()?.wave ?? 1)
    const waveProgress = Phaser.Math.Clamp(
      waveScene.getWaveProgress?.() ?? 0,
      0,
      1,
    )
    const waveBonus = Math.min(
      BALL_SPEED_RAMP_TUNING.maxWaveBonus,
      (wave - 1) * BALL_SPEED_RAMP_TUNING.perWaveBonus,
    )

    return (
      BALL_SPEED_RAMP_TUNING.initialSpeedRatio +
      waveBonus +
      waveProgress * BALL_SPEED_RAMP_TUNING.intraWaveBonus
    )
  }

  private getCurrentBaseSpeed(): number {
    let speed =
      this.launcher.ballSpeed *
      this.heroProfile.speedMultiplier *
      this.getDifficultySpeedRatio() *
      this.getProjectileSpeedMultiplier()

    if (this.launcher.reboundBehavior === "rail") speed *= 1.12
    if (this.launcher.reboundBehavior === "stationary") speed *= 0.2

    return speed
  }

  public getSpreadAngles(): number[] {
    const player = this.getPlayer()
    const shotCount = player?.stats.ballCount ?? 1
    const spreadAngles: number[] = []

    if (shotCount <= 1) {
      spreadAngles.push(0)
    } else {
      const spread = 15
      for (let i = 0; i < shotCount; i++) {
        const t = (i / (shotCount - 1)) * 2 - 1
        spreadAngles.push(t * spread)
      }
    }
    return spreadAngles
  }

  private getLaunchProfile(isOverdrive: boolean): {
    bonusHits: number
    isOverdrive: boolean
    spreadAngles: number[]
  } {
    const spreadAngles = this.getSpreadAngles()

    if (isOverdrive) {
      return {
        bonusHits: 3,
        isOverdrive: true,
        spreadAngles,
      }
    }

    return {
      bonusHits: 0,
      isOverdrive: false,
      spreadAngles,
    }
  }

  // ── Public API ────────────────────────────────────────────────

  /**
   * Called from flap(). Attempts to launch balls.
   * Returns true if balls were launched.
   */
  public tryLaunchOnFlap(
    playerX: number,
    playerY: number,
    _playerVx: number,
    _playerVy: number,
    heroAngle: number,
    pointerPosition?: { x: number; y: number } | null,
    isOverdrive = false,
    chargeLevel: 0 | 1 | 2 = 0,
  ): boolean {
    const profile = this.getLaunchProfile(isOverdrive)

    // REDESIGN: During overdrive, we use the provided heroAngle which is
    // calculated by WaveScene to account for gamepad, keyboard, or mouse.
    // We only fallback to mouse if heroAngle is 0 and mouse is active.
    let angleDeg = heroAngle

    if (isOverdrive && heroAngle === 0 && pointerPosition) {
      angleDeg = Phaser.Math.RadToDeg(
        Math.atan2(pointerPosition.y - playerY, pointerPosition.x - playerX),
      )
    }

    // Launch
    for (const spreadAngle of profile.spreadAngles) {
      this.launch(
        playerX,
        playerY,
        angleDeg + spreadAngle,
        profile.bonusHits,
        profile.isOverdrive,
        chargeLevel,
      )
    }
    return true
  }

  /**
   * Called automatically for secondary launchers.
   * Attempts to launch balls in the default launch angle.
   */
  public tryAutoFire(
    playerX: number,
    playerY: number,
    isOverdrive = false,
  ): boolean {
    const profile = this.getLaunchProfile(isOverdrive)

    // Launch in the fixed launch angle
    for (const spreadAngle of profile.spreadAngles) {
      this.launch(
        playerX,
        playerY,
        this.launcher.launchAngle + spreadAngle,
        profile.bonusHits,
        profile.isOverdrive,
      )
    }
    return true
  }

  /** Called every frame from WaveScene.update(). */
  public update(delta: number): void {
    for (const child of this.combatBallGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue

      const bd = getCombatBallData(sprite)
      if (!bd || bd.kind !== "combatBall") continue

      // Tick lifetime
      bd.lifetimeRemaining -= delta
      if (bd.lifetimeRemaining <= 0) {
        this.expireBall(sprite, bd)
        continue
      }

      // Skip all physics updates during explosion animation
      if (bd.isExploding) {
        this.updateBallVisuals(sprite, bd)
        continue
      }

      this.updateBallMovement(sprite, bd, delta)
      this.handleWallInteractions(sprite, bd)
      this.applyLauncherFrameBehavior()
      this.syncBallBody(sprite)
      this.updateBallVisuals(sprite, bd)
    }
  }

  /**
   * Called by WaveScene when the combat ball overlaps the player sprite.
   * Applies velocity-bias redirect.
   */
  public onPlayerContact(
    ballSprite: Phaser.Physics.Arcade.Sprite,
    playerSprite: Phaser.Physics.Arcade.Sprite,
  ): void {
    const bd = getCombatBallData(ballSprite)
    if (!bd || bd.kind !== "combatBall" || bd.isExploding) return

    const playerBody = playerSprite.body as Phaser.Physics.Arcade.Body
    const REDIRECT =
      this.heroProfile.redirectStrength *
      (this.launcher.reboundBehavior === "sticky" ? 1.22 : 1)

    const pvx = playerBody.velocity.x / 60
    const pvy = playerBody.velocity.y / 60

    const baseSpeed = this.getCurrentBaseSpeed()
    const speed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2) || baseSpeed

    const biasedVx = bd.vx + pvx * REDIRECT
    const biasedVy = bd.vy + pvy * REDIRECT

    const mag = Math.sqrt(biasedVx ** 2 + biasedVy ** 2) || 1
    bd.vx = (biasedVx / mag) * speed
    bd.vy = (biasedVy / mag) * speed

    if (
      this.launcher.reboundBehavior === "minimal" ||
      this.launcher.reboundBehavior === "rail"
    ) {
      bd.vy *= 0.55
    }
    if (bd.vx < 1) bd.vx = 1

    this.vfx.sparkEmitter.explode(4, ballSprite.x, ballSprite.y)
  }

  /**
   * Called by WaveScene when the combat ball overlaps an enemy.
   * Applies damage and decrements hit count.
   */
  public onEnemyContact(
    ballSprite: Phaser.Physics.Arcade.Sprite,
    enemySprite: Phaser.Physics.Arcade.Sprite,
  ): void {
    const bd = getCombatBallData(ballSprite)
    const ed = getEnemyData(enemySprite)

    if (!bd || bd.kind !== "combatBall" || bd.isExploding) return
    if (!ed || ed.kind !== "enemy" || !enemySprite.active) return

    // Prevent multi-frame overlap from registering as multiple hits on the same target
    const now = this.scene.time.now
    const rehitCooldown = 260
    const lastHit = bd.recentHitTargets.get(enemySprite) ?? 0
    if (now - lastHit < rehitCooldown) return
    bd.recentHitTargets.set(enemySprite, now)

    const interaction = ed.ballInteraction ?? "normal"
    let damage = bd.damage

    let impactColor: number = COLOR.warning
    let impactScale = 0.7
    let extraHitDamage = 0

    if (interaction === "armored") {
      const speed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2)
      const baseSpeed = this.getCurrentBaseSpeed()
      if (speed < baseSpeed * 1.15) {
        damage *= 0.5
      }
    }

    const isCrit = Math.random() < this.getCritChance()
    if (isCrit) {
      damage *= this.getCritDamageMultiplier()
      extraHitDamage *= this.getCritDamageMultiplier()
      impactColor = SCENE_TUNING.critTint
      impactScale *= SCENE_TUNING.critScale
    }

    const isBoss = ed.bossMaxHp !== undefined || ed.bossWave !== undefined
    if (isBoss && bd.isOverdrive) {
      damage = Math.max(1, Math.round(damage * OVERDRIVE_BOSS_DAMAGE_SCALE))
      extraHitDamage = Math.max(
        0,
        Math.round(extraHitDamage * OVERDRIVE_BOSS_DAMAGE_SCALE),
      )
    }

    ed.lastDamageReceived = damage + extraHitDamage

    // Boss-specific damage routing (e.g. brain shield absorption)
    if (isBoss && this.onBossHit) {
      const absorbed = this.onBossHit(
        enemySprite as GameSprite,
        Math.round(damage + extraHitDamage),
      )
      if (absorbed) {
        // Shield absorbed the hit — show feedback but skip hp deduction and death check
        this.vfx.emitImpactBurst(
          ballSprite.x,
          ballSprite.y,
          impactColor,
          impactScale,
        )
        bd.hitsRemaining--
        bd.vx = -bd.vx
        if (bd.vx > -1) bd.vx = -1
        if (bd.hitsRemaining <= 0) this.expireBall(ballSprite as GameSprite, bd)
        return
      }
    }

    if (ed.supportGateActive) {
      damage = Math.max(1, Math.round(damage * 0.2))
      extraHitDamage = Math.round(extraHitDamage * 0.2)
      impactColor = COLOR.info
    }

    ed.hp = Math.max(0, ed.hp - damage)
    if (extraHitDamage > 0) {
      ed.hp = Math.max(0, ed.hp - extraHitDamage)
      this.vfx.showDamageNumber(
        enemySprite.x + 8,
        enemySprite.y - 6,
        extraHitDamage,
        impactColor,
        isCrit,
      )
    }

    // Hit feedback — impact burst + damage number + enemy flash with HP-based intensity
    this.vfx.emitImpactBurst(
      ballSprite.x,
      ballSprite.y,
      impactColor,
      impactScale,
    )
    this.vfx.showDamageNumber(
      enemySprite.x,
      enemySprite.y - 15,
      damage,
      impactColor,
      isCrit,
    )
    const maxHp = ENEMY_DEFINITIONS[ed.type]?.hpBase ?? 100
    const hpPercent = Math.max(0, ed.hp / maxHp)
    this.vfx.flashEnemyHit(enemySprite, impactColor, hpPercent)

    // Decrement ball hits
    bd.hitsRemaining--

    // Pierce or reflect
    if (this.launcher.reboundBehavior === "rail") {
      // Ball passes through — no velocity change
    } else if (this.launcher.reboundBehavior === "knockback") {
      bd.vx = -bd.vx * 1.12
      if (bd.vx > -1) bd.vx = -1
    } else {
      bd.vx = -bd.vx
      if (bd.vx > -1) bd.vx = -1
    }

    // Check enemy death
    if (ed.hp <= 0) {
      this.onEnemyDefeated(enemySprite as GameSprite)
    }

    // Check ball expiry
    if (bd.hitsRemaining <= 0) {
      this.expireBall(ballSprite as GameSprite, bd)
    }
  }

  /**
   * Called by WaveScene when the combat ball overlaps a bumper.
   * Applies pinball rebound and grants +1 hit to the ball.
   */
  public onBumperContact(
    ballSprite: Phaser.Physics.Arcade.Sprite,
    bumperSprite: Phaser.Physics.Arcade.Sprite,
  ): void {
    const bd = getCombatBallData(ballSprite)
    const bumperEd = getBumperData(bumperSprite)

    if (!bd || bd.kind !== "combatBall" || bd.isExploding) return
    if (!bumperEd || bumperEd.kind !== "bumper") return

    // Prevent same bumper from triggering multiple times while ball exits its hitbox
    const nowB = this.scene.time.now
    const lastBumperHit = bd.recentHitTargets.get(bumperSprite) ?? 0
    if (nowB - lastBumperHit < 300) return
    bd.recentHitTargets.set(bumperSprite, nowB)

    const baseSpeed = this.getCurrentBaseSpeed()
    const currentSpeed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2) || baseSpeed

    const dx = ballSprite.x - bumperSprite.x
    const dy = ballSprite.y - bumperSprite.y
    const distance = Math.sqrt(dx * dx + dy * dy)
    const angle = Math.atan2(dy, dx)

    // ── Separation ───────────────────────────────────────────
    // If the ball is inside the bumper radius, push it out to prevent multi-frame sticking.
    const player = this.getPlayer()
    const ballSizeMultiplier = player?.stats.ballSize ?? 1.0
    const ballRadius =
      (BALL_TYPE_DEFS.fireball.displaySize / 2) * ballSizeMultiplier
    const minDistance = bumperEd.radius + ballRadius + 2
    if (distance < minDistance) {
      const pushX = bumperSprite.x + Math.cos(angle) * minDistance
      const pushY = bumperSprite.y + Math.sin(angle) * minDistance
      ballSprite.setPosition(pushX, pushY)
      bd.x = pushX
      bd.y = pushY
    }

    // ── Slingshot deflector ───────────────────────────────────
    if (bumperEd.slingshotAngle !== undefined) {
      const kickSpeed =
        Math.max(currentSpeed, baseSpeed) * SLINGSHOT_KICK_SPEED_MULT
      bd.vx = Math.cos(bumperEd.slingshotAngle) * kickSpeed
      bd.vy = Math.sin(bumperEd.slingshotAngle) * kickSpeed
      if (bd.vx < 0) bd.vx = Math.abs(bd.vx)

      this.vfx.emitImpactBurst(ballSprite.x, ballSprite.y, 0x84cc16, 0.8)
      this.vfx.sparkEmitter.explode(8, ballSprite.x, ballSprite.y)
      return
    }

    // ── Normal round bumper rebound ───────────────────────────
    const popSpeed = Math.max(currentSpeed, baseSpeed * BUMPER_POP_SPEED_MULT)
    bd.vx = Math.cos(angle) * popSpeed
    bd.vy = Math.sin(angle) * popSpeed
    if (Math.abs(bd.vx) < 0.5) bd.vx = bd.vx < 0 ? -0.5 : 0.5

    this.vfx.emitImpactBurst(ballSprite.x, ballSprite.y, COLOR.info, 0.5)
  }

  public getActiveBallCount(): number {
    return this.countActiveBalls()
  }

  public getCurrentBallType(): BallType {
    return "fireball"
  }

  // ── Private helpers ───────────────────────────────────────────

  private launch(
    spawnX: number,
    spawnY: number,
    angleDeg: number,
    bonusHits = 0,
    isOverdrive = false,
    chargeLevel: 0 | 1 | 2 = 0,
  ): void {
    const typeDef = BALL_TYPE_DEFS.fireball
    const ball = this.combatBallGroup.get(
      spawnX + 40,
      spawnY,
      typeDef.textureKey,
    ) as GameSprite | null
    if (!ball) return

    ball.setActive(true)
    ball.setVisible(true)
    ball.setDepth(90)
    const player = this.getPlayer()
    const chargeSizeMult =
      chargeLevel === 2 ? 2.5 : chargeLevel === 1 ? 1.5 : 1.0
    const ballSizeMultiplier = (player?.stats.ballSize ?? 1.0) * chargeSizeMult
    ball.clearTint()

    const body = ball.body as Phaser.Physics.Arcade.Body
    if (body) {
      body.enable = true
      body.setAllowGravity(false)
      // Disable Phaser's body movement integration — all ball movement is
      // manual (sprite.x += vx * delta). Without this, Phaser's postUpdate
      // computes (body.position - prevFrame) and re-applies it to sprite.x/y,
      // doubling every movement and causing balls to tunnel into walls.
      body.moves = false
      const forgivenessRadius: Record<string, number> = {
        very_low: 7,
        low: 9,
        medium: 11,
        high: 14,
      }
      const visualRadius = (typeDef.displaySize / 2) * ballSizeMultiplier
      const radius = Math.max(
        forgivenessRadius[this.heroProfile.forgiveness] ?? 9,
        visualRadius,
      )
      body.setCircle(radius)
    }

    const chargeSpeedMult =
      chargeLevel === 2 ? 2.0 : chargeLevel === 1 ? 1.5 : 1.0
    const baseSpeed = this.getCurrentBaseSpeed() * chargeSpeedMult
    const angleRad = Phaser.Math.DegToRad(angleDeg)
    const vx =
      this.launcher.reboundBehavior === "stationary"
        ? 0.35
        : Math.cos(angleRad) * baseSpeed
    const vy =
      this.launcher.reboundBehavior === "stationary"
        ? 0
        : Math.sin(angleRad) * baseSpeed

    const totalBounces = player?.stats.ballBounces ?? 0
    const chargeBonus = chargeLevel === 2 ? 2 : chargeLevel === 1 ? 1 : 0
    const totalHits = 1 + totalBounces + bonusHits + chargeBonus

    const bd: CombatBallEntityData = {
      kind: "combatBall",
      vx,
      vy,
      x: spawnX + 40,
      y: spawnY,
      ballType: "fireball",
      damage: this.getBallDamage(ballSizeMultiplier),
      hitsRemaining: totalHits,
      maxHits: totalHits,
      lifetimeRemaining: typeDef.lifetime,
      bounceCount: 0,
      launcherId: this.launcher.type as string,
      launchOrder: this.launchCounter++,
      isOverdrive,
      recentHitTargets: new Map(),
      ballSizeMultiplier,
    }
    setCombatBallData(ball, bd)

    // Launch VFX
    if (isOverdrive) {
      this.vfx.emitOverdriveFire(spawnX + 40, spawnY, angleRad)
    } else {
      this.vfx.emitMuzzleFlash(
        spawnX + 40,
        spawnY,
        angleRad,
        FIREBALL_COLOR,
        0.6 + chargeLevel * 0.5,
      )
    }
    EventBus.emit(EVENT.BALL_LAUNCHED, {
      ballType: "fireball",
      x: spawnX,
      y: spawnY,
    })
  }

  private expireBall(
    sprite: GameSprite,
    bd: CombatBallEntityData,
    forceExplosion = false,
    silent = false,
  ): void {
    const x = sprite.x
    const y = sprite.y

    // Mark as exploding to prevent collisions during explosion anim
    if (forceExplosion) {
      bd.isExploding = true
    }

    sprite.setActive(false)
    sprite.setVisible(false)

    // Disable the physics body so it's removed from the overlap/collision
    // broadphase. Without this the body persists invisibly and keeps
    // triggering hits against enemies, bumpers, and other balls.
    // Also hide it from the debug renderer — enable=false alone doesn't
    // suppress the debug outline.
    const body = sprite.body as Phaser.Physics.Arcade.Body
    if (body) {
      body.enable = false
      body.debugShowBody = false
    }

    if (forceExplosion) {
      this.applySplashDamage(
        x,
        y,
        bd.damage * SPLASH_DAMAGE_RATIO,
        MASSIVE_SPLASH_RADIUS,
        bd.isOverdrive,
      )
      this.vfx.emitExplosionBurst(x, y, FIREBALL_COLOR, 1.5)
      if (!silent) {
        this.vfx.shakeCamera(50, 0.002)
      }
    } else {
      this.vfx.emitExplosionBurst(x, y, FIREBALL_COLOR, 0.5)
    }

    if (!silent) {
      EventBus.emit(EVENT.BALL_EXPIRED, { ballType: bd.ballType, x, y })
    }
  }

  public expireAll(silent = false): void {
    const toExpire: { sprite: GameSprite; bd: CombatBallEntityData }[] = []

    for (const child of this.combatBallGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue
      const bd = getCombatBallData(sprite)
      if (bd && bd.kind === "combatBall") {
        toExpire.push({ sprite, bd })
      }
    }

    if (toExpire.length > 0 && !silent) {
      this.vfx.shakeCamera(200, 0.008)
    }

    for (const item of toExpire) {
      this.expireBall(item.sprite, item.bd, true, silent) // Force overdrive explosion
    }
  }

  private countActiveBalls(): number {
    let count = 0
    for (const child of this.combatBallGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue
      const bd = getCombatBallData(sprite)
      if (bd?.kind === "combatBall") count++
    }
    return count
  }

  private updateBallMovement(
    sprite: GameSprite,
    bd: CombatBallEntityData,
    delta: number,
  ): void {
    sprite.x += bd.vx * (delta / 16)
    sprite.y += bd.vy * (delta / 16)
    bd.x = sprite.x
    bd.y = sprite.y
    // Apply speed scaling AFTER movement so wall interactions and bumper
    // contacts from the previous frame are accounted for first.
    this.applySpeedScaling(bd, delta)
    this.syncBallBody(sprite)
  }

  private applySpeedScaling(bd: CombatBallEntityData, delta: number): void {
    const baseSpeed = this.getCurrentBaseSpeed()
    const railBonus = this.launcher.reboundBehavior === "rail" ? 1.18 : 1
    const targetSpeed = baseSpeed * railBonus
    const currentSpeed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2)
    if (currentSpeed <= 0) return

    if (this.launcher.reboundBehavior === "stationary") {
      const capSpeed = Math.max(0.18, baseSpeed * 0.4)
      if (currentSpeed > capSpeed) {
        const scale = capSpeed / currentSpeed
        bd.vx *= scale
        bd.vy *= scale
      }
      return
    }

    const minSpeed = baseSpeed * MIN_SPEED_RATIO

    if (currentSpeed < minSpeed) {
      // Below floor — boost immediately to minimum (never let ball stall)
      const scale = minSpeed / currentSpeed
      bd.vx *= scale
      bd.vy *= scale
    } else if (currentSpeed > targetSpeed * 1.01) {
      // Above target — apply gentle drag toward target speed.
      // BALL_OVERSPEED_DECAY is a per-frame multiplier close to 1, so bumper/slingshot
      // speed boosts persist for a noticeable duration instead of vanishing instantly.
      const frames = delta / 16
      const overspeedDecay =
        this.launcher.reboundBehavior === "rail"
          ? Math.min(0.9985, BALL_OVERSPEED_DECAY + 0.002)
          : BALL_OVERSPEED_DECAY
      const decay = overspeedDecay ** frames
      const newSpeed = targetSpeed + (currentSpeed - targetSpeed) * decay
      const scale = newSpeed / currentSpeed
      bd.vx *= scale
      bd.vy *= scale
    }
    // Within 1% of target — no correction, let it ride
  }

  private syncBallBody(sprite: GameSprite): void {
    const body = sprite.body as Phaser.Physics.Arcade.Body
    if (!body) return
    // Since body.moves = false, Phaser won't apply body->sprite deltas.
    // We manually keep body.position in sync with the sprite so overlap
    // detection (circle vs circle / circle vs rect) uses the correct location.
    body.position.set(sprite.x - body.halfWidth, sprite.y - body.halfHeight)
    body.prev.set(body.position.x, body.position.y)
    body.prevFrame.set(body.position.x, body.position.y)
    body.updateCenter()
  }

  private handleWallInteractions(
    sprite: GameSprite,
    bd: CombatBallEntityData,
  ): void {
    if (bd.isExploding) return

    // Top wall
    if (sprite.y <= BALL_WALL_PADDING) {
      bd.vy = Math.abs(bd.vy)
      sprite.y = BALL_WALL_PADDING
      bd.y = sprite.y
      this.enforceMinBounceAngle(bd, "horizontal")
      bd.bounceCount++
      bd.hitsRemaining--
      this.onWallBounce(sprite, "top")
      if (bd.hitsRemaining <= 0) {
        this.expireBall(sprite, bd)
        return
      }
    }

    // Bottom wall
    if (sprite.y >= GAME_HEIGHT - BALL_WALL_PADDING) {
      bd.vy = -Math.abs(bd.vy)
      sprite.y = GAME_HEIGHT - BALL_WALL_PADDING
      bd.y = sprite.y
      this.enforceMinBounceAngle(bd, "horizontal")
      bd.bounceCount++
      bd.hitsRemaining--
      this.onWallBounce(sprite, "bottom")
      if (bd.hitsRemaining <= 0) {
        this.expireBall(sprite, bd)
        return
      }
    }

    // Right wall
    if (sprite.x >= GAME_WIDTH - BALL_WALL_PADDING) {
      bd.vx = -Math.abs(bd.vx)
      sprite.x = GAME_WIDTH - BALL_WALL_PADDING
      bd.x = sprite.x
      bd.bounceCount++
      bd.hitsRemaining--
      this.onWallBounce(sprite, "right")
      if (bd.hitsRemaining <= 0) {
        this.expireBall(sprite, bd)
        return
      }
    }

    // Left wall — kickback: bounce with a speed boost and lifetime restore
    // Simulates the pinball outlane kickback / saver mechanism.
    if (sprite.x <= BALL_WALL_PADDING) {
      bd.vx = Math.abs(bd.vx) * KICKBACK_SPEED_MULT
      sprite.x = BALL_WALL_PADDING + 2
      bd.x = sprite.x
      bd.lifetimeRemaining = Math.min(
        bd.lifetimeRemaining + KICKBACK_LIFETIME_RESTORE,
        BALL_TYPE_DEFS.fireball.lifetime,
      )
      bd.bounceCount++
      bd.hitsRemaining--
      this.onKickback(sprite)
      if (bd.hitsRemaining <= 0) {
        this.expireBall(sprite, bd)
        return
      }
    }
  }

  private applyLauncherFrameBehavior(): void {
    // All balls use stable behavior: natural physics with no special effects
    // This keeps mechanics simple while still allowing rich gameplay via bumpers and walls
  }

  private updateBallVisuals(
    sprite: GameSprite,
    bd: CombatBallEntityData,
  ): void {
    const travelAngle = Math.atan2(bd.vy, bd.vx)

    sprite.setRotation(travelAngle)

    // Lifetime warning: flash in last 2s
    if (bd.lifetimeRemaining < 2000) {
      const flash = Math.sin(this.scene.time.now * 0.015) > 0
      sprite.setAlpha(flash ? 0.4 : 1)
    } else {
      sprite.setAlpha(1)
    }

    const speed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2)
    const trailScale = (bd.isOverdrive ? 1.25 : 0.9) * bd.ballSizeMultiplier
    const trailLength = bd.isOverdrive ? 14 : 10
    const travelX = speed > 0.001 ? bd.vx / speed : 1
    const travelY = speed > 0.001 ? bd.vy / speed : 0
    const trailX = sprite.x - travelX * trailLength
    const trailY = sprite.y - travelY * trailLength

    // Hot trail stamps make the orb feel like it is burning through the field.
    this.vfx.drawPersistentTrail(trailX, trailY, FIREBALL_COLOR, trailScale)
    this.vfx.drawPersistentTrail(
      sprite.x,
      sprite.y,
      FIREBALL_CORE,
      trailScale * 0.55,
    )
    this.vfx.drawPersistentTrail(
      trailX,
      trailY,
      FIREBALL_FLARE,
      trailScale * 0.28,
    )

    if (bd.isOverdrive) {
      // Pulsing scale for "power" feel
      const pulse = 1 + Math.sin(this.scene.time.now * 0.02) * 0.15
      sprite.setScale(pulse * bd.ballSizeMultiplier)
    } else if (speed > 0.4) {
      sprite.setScale(bd.ballSizeMultiplier)
    }

    if (speed > 0.75 && Math.random() > (bd.isOverdrive ? 0.7 : 0.9)) {
      this.vfx.sparkEmitter.explode(1, trailX, trailY)
    }
  }

  private onWallBounce(
    sprite: GameSprite,
    edge: "top" | "bottom" | "right" | "left",
  ): void {
    this.vfx.sparkEmitter.explode(4, sprite.x, sprite.y)
    EventBus.emit(EVENT.BALL_BOUNCE, { x: sprite.x, y: sprite.y, edge })
  }

  /** Left-wall kickback: bigger burst + distinct event */
  private onKickback(sprite: GameSprite): void {
    this.vfx.sparkEmitter.explode(12, sprite.x, sprite.y)
    this.vfx.emitImpactBurst(sprite.x, sprite.y, 0x84cc16, 0.9)
    EventBus.emit(EVENT.BALL_BOUNCE, {
      x: sprite.x,
      y: sprite.y,
      edge: "left",
      kickback: true,
    })
  }

  /**
   * After a top/bottom wall bounce, enforce a minimum vertical angle.
   * This prevents the ball from getting locked in a near-horizontal path
   * (the pinball equivalent of a "dead flat" trajectory that never hits anything).
   * Speed is preserved — only the direction changes.
   */
  private enforceMinBounceAngle(
    bd: CombatBallEntityData,
    wall: "horizontal",
  ): void {
    void wall
    const speed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2)
    if (speed === 0) return
    const absVy = Math.abs(bd.vy)
    const absVx = Math.abs(bd.vx)
    const minVy = absVx * MIN_BOUNCE_VY_FRACTION
    if (absVy < minVy) {
      bd.vy = Math.sign(bd.vy || 1) * minVy
      // Re-normalize to preserve speed so we don't change kinetic energy
      const newSpeed = Math.sqrt(bd.vx ** 2 + bd.vy ** 2)
      const scale = speed / newSpeed
      bd.vx *= scale
      bd.vy *= scale
    }
  }

  private applySplashDamage(
    cx: number,
    cy: number,
    damage: number,
    radius: number,
    isOverdrive: boolean,
  ): void {
    const waveScene = this.scene as Phaser.Scene & {
      enemyGroup?: Phaser.Physics.Arcade.Group
    }
    const enemyGroup = waveScene.enemyGroup
    if (!enemyGroup) return

    for (const child of enemyGroup.children) {
      const e = child as GameSprite
      if (!e.active) continue
      const ed = getEnemyData(e)
      if (!ed || ed.kind !== "enemy") continue

      const dist = Phaser.Math.Distance.Between(cx, cy, e.x, e.y)
      if (dist <= radius && dist > 0) {
        const isBoss = ed.bossMaxHp !== undefined || ed.bossWave !== undefined
        const appliedDamage =
          isBoss && isOverdrive
            ? Math.max(1, Math.round(damage * OVERDRIVE_BOSS_DAMAGE_SCALE))
            : damage

        if (isBoss && this.onBossHit?.(e, Math.round(appliedDamage))) {
          continue
        }

        ed.hp = Math.max(0, ed.hp - appliedDamage)
        if (ed.hp <= 0) {
          this.onEnemyDefeated(e)
        }
      }
    }
  }
}
